import { useMemo } from "react"
import { distinct, flatJoin, numberFormat } from "shared/util"
import { useUserSession } from "features/session/context"
import { hiddenFlags } from 'features/search/util'

import { SearchDetailResult } from "interface/search"
import { SisetuResult } from "interface/sisetu"
import { KojinOrgan, KojinResult } from "interface/kojin"

import { GridColumnDefMap } from "components/grid/types"
import { columnBase } from "components/grid/util"
import { renderer } from "components/grid/renderer"

export type SisetuWithKolList = SisetuResult & {
	kolList: {
		kojin?: KojinResult
		organ?: KojinOrgan
	}[]
}

type useSisetuGridColumnsProps = {
	search: SearchDetailResult
	onClickLink: (sisetuCode: string) => void
}
export const useSisetuGridColumns = ({
	search,
	onClickLink
}: useSisetuGridColumnsProps) => {
	const session = useUserSession()
	const {
		hideResult,
		hideDPC,
		hideCancer,
		hideJournal,
		hideTrial,
		hideResearch,
		hideSenmoni,
		hideConvention,
		hideGuideLine
	} = useMemo(
		() => hiddenFlags(search),
		[search]
	)
	const columnsDef = useMemo<GridColumnDefMap<SisetuWithKolList>>(
		() => {
			const columnsDef: GridColumnDefMap<SisetuWithKolList> = {
				'link': {
					headerName: '詳細',
					hideable: false,
					sticky: true,
					value: (r) => r.sisetuCode,
					...columnBase.action.openByValue(onClickLink)
				},
				'rank': {
					headerName: '総合順位',
					hidden: hideResult,
					value: (r) => r.score?.rank,
					...columnBase.rank
				},
				'sisetuName': {
					headerName: '施設名',
					width: 400,
					hideable: false,
					sticky: true,
					value: (r) => [
						r.sisetuNameKana ?? '',
						r.sisetuName ?? ''
					],
					hasKana: true,
					filterType: 'text'
				},
				'ryakusikiSisetuName': {
					headerName: '施設名(略式)',
					width: 200,
					value: (r) => [
						r.ryakusikiSisetuNameKana ?? '',
						r.ryakusikiSisetuName ?? ''
					],
					defaultShow: false,
					hasKana: true,
					filterType: 'text'
				},
				'block': {
					headerName: 'ブロック',
					width: 100,
					value: (r) => r.juusyoBlockName,
					filterType: 'candidates'
				},
				'todofuken': {
					headerName: '都道府県',
					defaultShow: false,
					width: 100,
					value: (r) => r.juusyoKenName,
					filterType: 'candidates'
				},
				'iryoken': {
					headerName: '医療圏',
					width: 100,
					defaultShow: false,
					value: (r) => r.iryoukenName,
					filterType: 'candidates'
				},
				'city': {
					headerName: '市区町村',
					width: 100,
					defaultShow: false,
					value: (r) => r.cityName,
					filterType: 'candidates'
				},
				'juusyo': {
					headerName: '施設住所',
					width: 300,
					defaultShow: false,
					value: (r) => r.juusyo,
					filterType: 'text'
				},
				'bedCount': {
					headerName: '病床数',
					value: (r) => r.bedCount?.ippan,
					filterType: 'range',
					suffix: '床',
					...columnBase.bedCount
				},
				'byouinSyubetu': {
					headerName: '種別',
					width: 70,
					defaultShow: false,
					value: (r) => r.byouinSyubetu?.shortName,
					filterType: 'candidates'
				},
				'keieitai': {
					headerName: '経営体',
					width: 70,
					defaultShow: false,
					value: (r) => r.keieitai?.shortName,
					filterType: 'candidates'
				},
				'sisetuKubun': {
					headerName: '施設区分',
					width: 120,
					value: (r) => r.sisetuKubun?.name,
					filterType: 'candidates'
				},
				'hospitalFlags': {
					headerName: '施設機能',
					width: 160,
					value: (r) => r.hospitalFlags?.map(flag => flag.functionName),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'kojinCount': {
					headerName: 'DCF医師数',
					defaultShow: false,
					value: (r) => r.kojinCount,
					filterType: 'range',
					...columnBase.personCount
				},
				'pidCount': {
					headerName: 'CN会員数',
					hidden: session.isCareNet === false,
					defaultShow: false,
					value: (r) => r.pidCount,
					filterType: 'range',
					...columnBase.personCount
				},
				'pidRate': {
					headerName: 'CN会員率',
					hidden: session.isCareNet === false,
					defaultShow: false,
					value: (r) => (r.kojinCount ?? 0) > 0 ? ((r.pidCount ?? 0) / (r.kojinCount ?? 0)) : null,
					align: 'right',
					width: 80,
					type: 'number',
					valueFormatter: (value: number | string) => {
						if (typeof value !== 'number') return
						return (value * 100).toFixed(2) + ' %'
					}
				},
				'senmoniScore': {
					headerName: '専門医スコア',
					hidden: hideResult || hideSenmoni,
					value: (r) => r.score?.senmoni?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { senmoniRank?: number }) => {
						return (value && value !== Infinity && row.senmoniRank && row.senmoniRank !== Infinity) ? (
							`${numberFormat(value)}pt (${row.senmoniRank}位)`
						) : ''
					},
					suffix: 'pt',
					subField: 'senmoniRank',
					filterType: 'histgram'
				},
				'senmoniRank': {
					headerName: '専門医順位',
					hidden: true,
					defaultShow: false,
					value: (r) => r.score?.senmoni?.rank,
					...columnBase.rank,
				},
				'dpcScore': {
					headerName: 'DPCスコア',
					hidden: hideResult || hideDPC,
					value: (r) => r.score?.dpc?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { dpcRank?: number }) => {
						return (value && value !== Infinity && row.dpcRank && row.dpcRank !== Infinity) ? (
							`${numberFormat(value)}件 (${row.dpcRank}位)`
						) : ''
					},
					suffix: '件',
					subField: 'dpcRank',
					filterType: 'histgram',
					histgram: { unit: 100 }
				},
				'dpcRank': {
					headerName: 'DPC順位',
					hidden: true,
					value: (r) => r.score?.dpc?.rank,
					...columnBase.rank
				},
				'cancerScore': {
					headerName: 'がんスコア',
					hidden: hideResult || hideCancer,
					value: (r) => r.score?.cancer?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { cancerRank?: number }) => {
						return (value && value !== Infinity && row.cancerRank && row.cancerRank !== Infinity) ? (
							`${numberFormat(Math.floor(value * 100) / 100)}pt (${row.cancerRank}位)`
						) : ''
					},
					suffix: '件',
					subField: 'cancerRank',
					filterType: 'histgram'
				},
				'cancerRank': {
					headerName: 'がん順位',
					hidden: true,
					value: (r) => r.score?.cancer?.rank,
					...columnBase.rank
				},
				'cancerStage0count': {
					headerName: 'Stage0',
					hidden: hideResult || hideCancer,
					defaultShow: false,
					value: (r) => r.score?.cancer?.stage0count,
					...columnBase.count
				},
				'cancerStage1count': {
					headerName: 'Stage1',
					hidden: hideResult || hideCancer,
					defaultShow: false,
					value: (r) => r.score?.cancer?.stage1count,
					...columnBase.count
				},
				'cancerStage2count': {
					headerName: 'Stage2',
					hidden: hideResult || hideCancer,
					defaultShow: false,
					value: (r) => r.score?.cancer?.stage2count,
					...columnBase.count
				},
				'cancerStage3count': {
					headerName: 'Stage3',
					hidden: hideResult || hideCancer,
					defaultShow: false,
					value: (r) => r.score?.cancer?.stage3count,
					...columnBase.count
				},
				'cancerStage4count': {
					headerName: 'Stage4',
					hidden: hideResult || hideCancer,
					defaultShow: false,
					value: (r) => r.score?.cancer?.stage4count,
					...columnBase.count
				},
				'trialScore': {
					headerName: '治験・研究スコア',
					hidden: hideResult || hideTrial,
					value: (r) => r.score?.trial?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { trialRank?: number }) => {
						return (value && value !== Infinity && row.trialRank && row.trialRank !== Infinity) ? (
							`${numberFormat(value)}件 (${row.trialRank}位)`
						) : ''
					},
					suffix: '件',
					subField: 'trialRank',
					filterType: 'histgram'
				},
				'trialRank': {
					headerName: '治験・研究順位',
					hidden: true,
					value: (r) => r.score?.trial?.rank,
					...columnBase.rank
				},
				'guidelineScore': {
					headerName: 'ガイドラインスコア',
					hidden: hideResult || hideGuideLine,
					value: (r) => r.score?.guideline?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { guidelineRank?: number }) => {
						return (value && value !== Infinity && row.guidelineRank && row.guidelineRank !== Infinity) ? (
							`${numberFormat(value)}pt (${row.guidelineRank}位)`
						) : ''
					},
					suffix: 'pt',
					subField: 'guidelineRank',
					filterType: 'histgram'
				},
				'guidelineRank': {
					headerName: 'ガイドライン順位',
					hidden: true,
					value: (r) => r.score?.guideline?.rank,
					...columnBase.rank
				},
				'journalScore': {
					headerName: '論文スコア',
					hidden: hideResult || hideJournal,
					value: (r) => r.score?.journal?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { journalRank?: number }) => {
						return (value && value !== Infinity && row.journalRank && row.journalRank !== Infinity) ? (
							`${numberFormat(value)}pt (${row.journalRank}位)`
						) : ''
					},
					suffix: 'pt',
					subField: 'journalRank',
					filterType: 'histgram'
				},
				'journalRank': {
					headerName: '論文順位',
					hidden: true,
					value: (r) => r.score?.journal?.rank,
					...columnBase.rank
				},
				'researchScore': {
					headerName: '研究スコア',
					hidden: hideResult || hideResearch,
					defaultShow: false,
					value: (r) => r.score?.research?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { researchRank?: number }) => {
						return (value && value !== Infinity && row.researchRank && row.researchRank !== Infinity) ? (
							`${numberFormat(value)}pt (${row.researchRank}位)`
						) : ''
					},
					suffix: 'pt',
					subField: 'researchRank',
					filterType: 'histgram'
				},
				'researchRank': {
					headerName: '研究順位',
					hidden: true,
					value: (r) => r.score?.research?.rank,
					...columnBase.rank
				},
				'convention': {
					headerName: '学会',
					width: 300,
					hidden: hideResult || hideConvention,
					value: (r) => distinct(r.kolList?.flatMap(
						kol => kol.kojin?.convention?.flatMap(
							cnv => cnv.score > 0 ? flatJoin('\t',
								cnv.numberOfTimes,
								cnv.conventionName?.replaceAll('　', ' '),
								cnv.role
							) : []
						) ?? []
					)),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: "text"
				},
				'kolTop5': {
					headerName: 'KOL医師',
					hidden: hideResult,
					width: 300,
					value: (r) => r.kolList.flatMap(
						kol => kol.kojin ? flatJoin('\t',
							kol.kojin.isiName,
							kol.kojin.score?.rank ? `(${kol.kojin.score.rank}位)` : null,
							kol.organ?.bukaName,
							kol.organ?.yakusyokuName
						) : []
					),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					limitRow: 5,
					filterType: 'text'
				},
				'sisetuCode': {
					headerName: 'DCF施設コード',
					width: 100,
					defaultShow: false,
					value: (r) => r.sisetuCode,
					filterType: 'text'
				},
				'institutionalCode': {
					headerName: '施設コード',
					width: 120,
					defaultShow: false,
					value: (r) => r.institutionalCode,
					filterType: 'text'
				}
			}
			if (hideResult === false && hideGuideLine === false) {
				const DEF = '__default_'
				const sisetuGuideLineTypes: string[] = []
				const kojinGuideLineTypes: string[] = []
				search.guideLine?.forEach(guideLine => {
					const guideLineType = guideLine.guideLineType ?? DEF
					if (guideLine.hasHospital && sisetuGuideLineTypes.includes(guideLineType) === false) {
						sisetuGuideLineTypes.push(guideLineType)
					}
					if (guideLine.hasDoctor && sisetuGuideLineTypes.includes(guideLineType) === false) {
						kojinGuideLineTypes.push(guideLineType)
					}
				})
				sisetuGuideLineTypes.sort((a, b) => a === DEF ? -1 : b === DEF ? 1 : a.localeCompare(b))
				kojinGuideLineTypes.sort((a, b) => a === DEF ? -1 : b === DEF ? 1 : a.localeCompare(b))

				sisetuGuideLineTypes.forEach((guideLineType) => {
					const field = guideLineType === DEF ? 'guideLineSisetu' : `guideLineSisetu-${guideLineType}`
					columnsDef[field] = {
						headerName: guideLineType === DEF ? '施設ガイドライン' : guideLineType,
						width: 300,
						value: (r) => r.guideLine?.flatMap(
							guide => {
								const type = search.guideLine?.find(
									guideLine => guideLine.guideLineId === guide.guideLineId
								)?.guideLineType ?? null
								if (guideLineType === DEF) {
									if (type !== null) return []
								} else {
									if (type !== guideLineType) return []
								}
								if (guide.score === 0) return []
								return flatJoin('\t',
									guide.guideLineName?.replaceAll('　', ' '),
									guide.hospitalClass?.replaceAll('　', ' ')
								)
							}
						),
						renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
						filterType: "text"
					}
				})

				kojinGuideLineTypes.forEach((guideLineType) => {
					const field = guideLineType === DEF ? 'guideLineKojin' : `guideLineKojin-${guideLineType}`
					columnsDef[field] = {
						headerName: guideLineType === DEF ? '個人ガイドライン' : guideLineType,
						width: 300,
						limitRow: 5,
						value: (r) => {
							const guideLineList: {
								guideLineId: number
								guideLineName: string
								postNames: string[]
								count: number
							}[] = []
							r.kolList?.forEach(kol => kol.kojin?.guideLine?.forEach(guide => {
								const type = search.guideLine?.find(
									guideLine => guideLine.guideLineId === guide.guideLineId
								)?.guideLineType ?? null

								if (guideLineType === DEF) {
									if (type !== null) return
								} else {
									if (type !== guideLineType) return
								}
								if (guide.score === 0) return

								if (guide.score > 0) {
									let exist = guideLineList.find(g => g.guideLineId === guide.guideLineId)
									if (!exist) {
										exist = {
											guideLineId: guide.guideLineId,
											guideLineName: guide.guideLineName,
											postNames: [],
											count: 0
										}
										guideLineList.push(exist)
									}
									if (guide.postNames) {
										if (!exist.postNames.includes(guide.postNames)) {
											exist.postNames.push(guide.postNames)
										}
									}
									exist.count = exist.count + 1
								}
							}))
							guideLineList.sort((a, b) => a.guideLineId - b.guideLineId)
							return guideLineList.map(guide => flatJoin('\t',
								`${guide.guideLineName.replaceAll('　', ' ')}`,
								`(${guide.count}人)`,
								guide.postNames.join('\t')
							))
						},
						renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
						filterType: "text"
					}
				})
			}
			return columnsDef
		},
		[search, session, hideResult, hideDPC, hideCancer, hideJournal, hideTrial, hideSenmoni, hideConvention, hideGuideLine, hideResearch, onClickLink]
	)
	return { columnsDef }
}
